/* hulkWishlistPLPButtons.js — Wishlist buttons for product cards, cart, and QuickBuy */

(() => {
  /* ---------------- Core (helpers, API, state) ---------------- */

  if (!window.WishlistCore) {
    const DEFAULT_WISHLIST_NAME = "My Wishlist";

    function createHeaders(includeEncryptToken = true) {
      const headers = {
        Authorization: window.hulkappsWishlist?.bearerToken || "",
        "Content-Type": "application/json",
        Accept: "application/json",
      };
      if (includeEncryptToken && window.hulkappsWishlist?.encryptToken) {
        headers["Encrypt-token"] = window.hulkappsWishlist.encryptToken;
      }
      return headers;
    }

    function createUrlWithParams(baseUrl, params) {
      const url = new URL(baseUrl);
      Object.keys(params || {}).forEach((k) => {
        const v = params[k];
        if (v !== undefined && v !== null) url.searchParams.append(k, v);
      });
      return url;
    }

    async function makeRequest(url, headers, options = {}) {
      const res = await fetch(url, { method: "GET", headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    }

    async function getDefaultWishlistId() {
      const cid = window.hulkappsWishlist?.customerId;
      if (!cid) return null; // guests don't have lists
      if (window.hulkappsWishlist.defaultWishlistId)
        return window.hulkappsWishlist.defaultWishlistId;

      const listsUrl = createUrlWithParams(
        "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/lists",
        { customer_id: cid }
      );
      const listsResp = await makeRequest(listsUrl, createHeaders());
      const lists = Array.isArray(listsResp)
        ? listsResp
        : listsResp?.data || [];
      let def =
        lists.find((l) => l.is_default) ||
        lists.find((l) => (l.name || "").trim() === DEFAULT_WISHLIST_NAME);

      if (!def) {
        const r = await fetch(
          "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/create",
          {
            method: "POST",
            headers: createHeaders(),
            body: JSON.stringify({
              name: DEFAULT_WISHLIST_NAME,
              customer_id: String(cid),
            }),
          }
        );
        if (!r.ok) throw new Error(`Create wishlist failed: ${r.status}`);
        const j = await r.json();
        def = { id: j?.data?.id };
      }

      window.hulkappsWishlist.defaultWishlistId = def.id;
      return def.id;
    }

    async function addProductToDefaultWishlist({ productId, variantId }) {
      const isLoggedIn = !!window.hulkappsWishlist?.customerId;

      if (isLoggedIn) {
        const listId = await getDefaultWishlistId();
        const res = await fetch(
          "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/add_product",
          {
            method: "POST",
            headers: createHeaders(),
            body: JSON.stringify({
              product_id: String(productId),
              customer_id: String(window.hulkappsWishlist.customerId),
              wishlist_ids: [Number(listId)], // array (not string)
              ...(variantId ? { variant_id: String(variantId) } : {}),
            }),
          }
        );
        if (!res.ok) {
          const errText = await res.text().catch(() => "");
          console.error("Add (customer) failed:", res.status, errText);
          if (res.status === 422 && /exist|already/i.test(errText))
            return { ok: true, already: true };
          throw new Error(`Add (customer) failed: ${res.status}`);
        }
        const result = await res.json();
        // Add the variant ID that was actually used to the response
        if (result?.data && variantId) {
          result.data.variant_id = variantId;
        }
        return result;
      } else {
        const gid = window.hulkappsWishlist?.guestId || getSavedGuestUuid();

        const res = await fetch(
          "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/add_product/guest",
          {
            method: "POST",
            headers: createHeaders(false),
            body: JSON.stringify({
              product_id: String(productId),
              ...(gid ? { uuid: gid } : {}),
              ...(variantId ? { variant_id: String(variantId) } : {}), // send only if we have a server uuid              ...(variantId ? { variant_id: String(variantId) } : {}),
            }),
          }
        );
        if (!res.ok) {
          const errText = await res.text().catch(() => "");
          console.error("Add (guest) failed:", res.status, errText);
          throw new Error(`Add (guest) failed: ${res.status}`);
        }
        // 204 = already existed; no body
        if (res.status === 204) return { ok: true, already: true };
        let data = {};
        try {
          data = await res.json();
        } catch (_) {}

        // Try to capture server-issued UUID from known spots
        const serverUuid =
          data?.data?.uuid ||
          data?.data?.guest_uuid ||
          data?.uuid ||
          data?.guest_uuid ||
          null;
        if (serverUuid && !window.hulkappsWishlist?.guestId) {
          saveGuestUuid(serverUuid);
        }
        // Add the variant ID that was actually used to the response
        if (data?.data && variantId) {
          data.data.variant_id = variantId;
        }
        return data;
      }
    }

    async function findWishlistContainingProduct({ productId, variantId }) {
      const cid = window.hulkappsWishlist?.customerId;
      if (!cid) return null;

      try {
        // Get all wishlists for the customer
        const listsUrl = createUrlWithParams(
          "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/lists",
          { customer_id: cid }
        );
        const listsResp = await makeRequest(listsUrl, createHeaders());
        const lists = Array.isArray(listsResp) ? listsResp : listsResp?.data || [];

        // Search through each wishlist to find the product
        for (const list of lists) {
          const productsUrl = createUrlWithParams(
            "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/products",
            {
              wishlist_id: list.id,
              customer_id: cid,
              status: "published",
            }
          );
          const productsResp = await makeRequest(productsUrl, createHeaders());
          const products = Array.isArray(productsResp?.data) ? productsResp.data : [];

          // Check if this wishlist contains the product with matching variant
          const foundProduct = products.find(product => {
            const matchesProduct = Number(product.product_id) === Number(productId);
            const matchesVariant = Number(variantId) 
              ? Number(product.variant_id) === Number(variantId)
              : !product.variant_id; // If no variant specified, match products without variant
            return matchesProduct && matchesVariant;
          });

          if (foundProduct) {
            return list.id;
          }
        }
        return null;
      } catch (err) {
        console.error("Error finding wishlist containing product:", err);
        return null;
      }
    }

    async function removeProductFromWishlist({ productId, variantId }) {
      const isLoggedIn = !!window.hulkappsWishlist?.customerId;

      if (isLoggedIn) {
        // Find which wishlist contains this product
        const listId = await findWishlistContainingProduct({ productId, variantId });
        
        if (!listId) {
          console.warn("Product not found in any wishlist:", productId, variantId);
          return { ok: true, notFound: true };
        }

        const res = await fetch(
          "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/delete_product",
          {
            method: "POST",
            headers: createHeaders(),
            body: JSON.stringify({
              product_id: String(productId),
              customer_id: String(window.hulkappsWishlist.customerId),
              wishlist_ids: [Number(listId)],
              ...(variantId ? { variant_id: String(variantId) } : {}),
            }),
          }
        );
        if (!res.ok) {
          const errText = await res.text().catch(() => "");
          console.error("Remove (customer) failed:", res.status, errText);
          throw new Error(`Remove (customer) failed: ${res.status}`);
        }
        return res.json();
      } else {
        // For guest users, remove from guest wishlist
        const gid = window.hulkappsWishlist?.guestId || getSavedGuestUuid();

        const res = await fetch(
          "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/delete_product/guest",
          {
            method: "POST",
            headers: createHeaders(false),
            body: JSON.stringify({
              product_id: String(productId),
              uuid: gid,
              ...(variantId ? { variant_id: String(variantId) } : {}),
            }),
          }
        );
        if (!res.ok) {
          const errText = await res.text().catch(() => "");
          console.error("Remove (guest) failed:", res.status, errText);
          throw new Error(`Remove (guest) failed: ${res.status}`);
        }
        return res.json();
      }
    }

    function markButtonAsAdded(anchorEl, actualVariantId = null) {
      if (!anchorEl) return;
      anchorEl.setAttribute("data-added", "true");

      const trigger = anchorEl.getAttribute("data-wishlist-trigger");
      const isTogglingButton = trigger === "card" || trigger === "quickbuy";
      if (!isTogglingButton) {
        anchorEl.href = `${window.shopUrl}/${window.hulkappsWishlist.generalSettings.app_proxy_prefix}/${window.hulkappsWishlist.generalSettings.app_proxy_path}`;
      } else {
        anchorEl.href = "#";
      }

      const pid = Number(anchorEl.getAttribute("data-proid"));
      let variantId = anchorEl.getAttribute("data-varid") ? Number(anchorEl.getAttribute("data-varid")) : null;
      
      // If we have an actual variant ID from the wishlist operation, use that
      if (actualVariantId) {
        variantId = actualVariantId;
        anchorEl.setAttribute("data-varid", actualVariantId);
      }
      
      if (!Number.isNaN(pid)) {
        const arr = (window.hulkappsWishlist.wishlistProductIds =
          window.hulkappsWishlist.wishlistProductIds || []);
        if (!arr.includes(pid)) arr.push(pid);
        
        // Also add to wishlistItems array
        const itemsArr = (window.hulkappsWishlist.wishlistItems =
          window.hulkappsWishlist.wishlistItems || []);
        const itemExists = itemsArr.some(
          item => item.productId === pid && item.variantId === variantId
        );
        if (!itemExists) {
          itemsArr.push({ productId: pid, variantId: variantId });
        }
      }
    }

    function markButtonAsRemoved(anchorEl) {
      if (!anchorEl) return;
      anchorEl.setAttribute("data-added", "false");
      anchorEl.href = "#";
      const pid = Number(anchorEl.getAttribute("data-proid"));
      const variantId = anchorEl.getAttribute("data-varid") ? Number(anchorEl.getAttribute("data-varid")) : null;
      
      if (!Number.isNaN(pid)) {
        const arr = window.hulkappsWishlist.wishlistProductIds || [];
        const index = arr.indexOf(pid);
        if (index > -1) arr.splice(index, 1);
        
        // Also remove from wishlistItems array
        const itemsArr = window.hulkappsWishlist.wishlistItems || [];
        const itemIndex = itemsArr.findIndex(
          item => item.productId === pid && item.variantId === variantId
        );
        if (itemIndex > -1) itemsArr.splice(itemIndex, 1);
      }
    }

    async function fetchWishlistProducts(wishlistId) {
      const url = createUrlWithParams(
        "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/products",
        {
          wishlist_id: wishlistId,
          customer_id: window.hulkappsWishlist.customerId,
          status: "published",
          sort_by_date: "desc",
        }
      );
      const resp = await makeRequest(url, createHeaders());
      (resp?.data || []).forEach((row) => {
        const pid = Number(row.product_id);
        const variantId = row.variant_id ? Number(row.variant_id) : null;
        
        // Store in product IDs array for backward compatibility
        if (!window.hulkappsWishlist.wishlistProductIds.includes(pid)) {
          window.hulkappsWishlist.wishlistProductIds.push(pid);
        }
        
        // Store full item data with variant ID
        const itemExists = window.hulkappsWishlist.wishlistItems.some(
          item => item.productId === pid && item.variantId === variantId
        );
        if (!itemExists) {
          window.hulkappsWishlist.wishlistItems.push({
            productId: pid,
            variantId: variantId
          });
        }
      });
    }

    function updateHeaderWishlistCount() {
      const totalCountElement = document.querySelector(
        "[data-totalwishlistitems]"
      );
      
      if (totalCountElement && window.hulkappsWishlist?.wishlistProductIds) {
        if (window.hulkappsWishlist.wishlistProductIds.length > 0) {
        totalCountElement.textContent =
          window.hulkappsWishlist.wishlistProductIds.length;
          totalCountElement.style.removeProperty("display");
          totalCountElement.style.display = "grid !important";
        }
        else {
          totalCountElement.textContent = "";
          totalCountElement.style.display = "none !important";
        }
      }
    }

    async function fetchWishlistData() {
      window.hulkappsWishlist.wishlistProductIds = [];
      window.hulkappsWishlist.wishlistItems = []; // Store full items with variant IDs
      window.hulkappsWishlist._wishlistLoaded = false;
      try {
        if (window.hulkappsWishlist.customerId) {
          const url = createUrlWithParams(
            "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/lists",
            { customer_id: window.hulkappsWishlist.customerId }
          );
          const listsResp = await makeRequest(url, createHeaders());
          const lists = Array.isArray(listsResp)
            ? listsResp
            : listsResp?.data || [];
          await Promise.all(lists.map((l) => fetchWishlistProducts(l.id)));
        } else {
          const cached = getSavedGuestUuid();
          if (cached) {
            saveGuestUuid(cached);
            const url = createUrlWithParams(
              "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/products/guest",
              { uuid: cached }
            );

            const resp = await makeRequest(url, createHeaders(false));
            (resp?.data || []).forEach((row) => {
              const pid = Number(row.product_id);
              const variantId = row.variant_id ? Number(row.variant_id) : null;
              
              // Store in product IDs array for backward compatibility
              if (!window.hulkappsWishlist.wishlistProductIds.includes(pid)) {
                window.hulkappsWishlist.wishlistProductIds.push(pid);
              }
              
              // Store full item data with variant ID
              const itemExists = window.hulkappsWishlist.wishlistItems.some(
                item => item.productId === pid && item.variantId === variantId
              );
              if (!itemExists) {
                window.hulkappsWishlist.wishlistItems.push({
                  productId: pid,
                  variantId: variantId
                });
              }
            });

            // Notify Hulk app that guest wishlist data is loaded
            try {
              window.HulkappWishlist?._updateWishlistButton?.();

              // Set additional Hulk app variables for guest wishlist
              if (window.HulkappWishlist) {
                window.HulkappWishlist.guestId =
                  window.hulkappsWishlist.guestId;
                window.HulkappWishlist.wishlistProductIds =
                  window.hulkappsWishlist.wishlistProductIds;
              }
            } catch (e) {
            }
          }
        }
      } catch (err) {
        console.error("Failed to fetch wishlist data:", err);
      } finally {
        window.hulkappsWishlist._wishlistLoaded = true;

        // Update header count on every page load after wishlist data is fetched
        updateHeaderWishlistCount();
      }
    }

    window.WishlistCore = {
      DEFAULT_WISHLIST_NAME,
      createHeaders,
      createUrlWithParams,
      makeRequest,
      getDefaultWishlistId,
      addProductToDefaultWishlist,
      removeProductFromWishlist,
      findWishlistContainingProduct,
      markButtonAsAdded,
      markButtonAsRemoved,
      fetchWishlistData,
      fetchWishlistProducts,
      ready: null,
    };

    window.WishlistCore.ready = fetchWishlistData();
  }

  const Core = window.WishlistCore;

  const GUEST_UUID_KEY = "hulk_wishlist_guest_uuid";
  function getSavedGuestUuid() {
    try {
      return localStorage.getItem(GUEST_UUID_KEY) || null;
    } catch {
      return null;
    }
  }

  function setCookie(name, value, opts = {}) {
    const parts = [`${name}=${encodeURIComponent(value || "")}`];
    parts.push(`path=${opts.path || "/"}`);
    if (opts.maxAge !== undefined) parts.push(`max-age=${opts.maxAge}`);
    if (opts.sameSite) parts.push(`SameSite=${opts.sameSite}`);
    if (location.protocol === "https:") parts.push("Secure");
    document.cookie = parts.join(";");
  }

  function deleteCookie(name, path = "/") {
    setCookie(name, "", { path, maxAge: 0, sameSite: "Lax" });
  }

  function saveGuestUuid(uuid) {
    if (!uuid) return;
    window.hulkappsWishlist = window.hulkappsWishlist || {};
    window.hulkappsWishlist.guestId = uuid;
    try {
      localStorage.setItem(GUEST_UUID_KEY, uuid);
    } catch {}
    // let Hulk set its own cookie if available
    try {
      window.HulkappWishlist?._manageGuestCookie?.(uuid);
    } catch {}
    // belt & suspenders
    setCookie("hulk_wishlist_UserUUID", uuid, {
      path: "/",
      maxAge: 31536000,
      sameSite: "Lax",
    });
  }

  function clearGuestState() {
    try {
      localStorage.removeItem(GUEST_UUID_KEY);
    } catch {}
    deleteCookie("hulk_wishlist_UserUUID", "/");
    // also clear any proxy-path cookie just in case
    const gs = window.hulkappsWishlist?.generalSettings || {};
    const proxyPath = `/${gs.app_proxy_prefix || "apps"}/${
      gs.app_proxy_path || "advanced-wishlist"
    }`;
    deleteCookie("hulk_wishlist_UserUUID", proxyPath);

    if (window.hulkappsWishlist) window.hulkappsWishlist.guestId = "";
  }

  function whenWishlistLoaded(cb) {
    // calls cb once after data has been fetched at least once
    if (window.hulkappsWishlist?._wishlistLoaded) return cb();
    const iv = setInterval(() => {
      if (window.hulkappsWishlist?._wishlistLoaded) {
        clearInterval(iv);
        cb();
      }
    }, 150);
    setTimeout(() => clearInterval(iv), 7000); // safety cap
  }

  function watchGuestIdAndLoadOnce() {
    if (window.hulkappsWishlist?._watchedGuestId) return;
    window.hulkappsWishlist._watchedGuestId = true;

    const iv = setInterval(async () => {
      const gid = window.hulkappsWishlist?.guestId;
      if (gid) {
        clearInterval(iv);
        try {
          await Core.fetchWishlistData(); // populate ids
        } catch (e) {}
        // mark loaded happens inside fetchWishlistData
        hydrateCartWishlistHearts(); // run once data is in
        hydrateCardWishlistHearts();
      }
    }, 200);
    setTimeout(() => clearInterval(iv), 8000); // don't poll forever
  }

  /* ---------------- Product card wishlist buttons ---------------- */

  // Helper function to find the variant ID for a product in the wishlist
  function findWishlistVariantId(productId) {
    const items = window.hulkappsWishlist?.wishlistItems || [];
    const item = items.find(item => item.productId === Number(productId));
    return item ? item.variantId : null;
  }

  // Helper function to detect wishlist source action
  function getWishlistSource() {
    const url = window.location.href.toLowerCase();
    const path = window.location.pathname.toLowerCase();
    
    if (path.includes("/products/") || document.querySelector('.main-product')) {
      return "PDP";
    } else if (path.includes("/collections/") || path.includes("/collection/")) {
      return "PLP";
    } else if (path.includes("/search") || url.includes("search?q=")) {
      return "Search";
    }
    return "Other";
  }
  
  // Helper function to get category (if available from analytics snippet)
  function getCategory(itemCategory, itemCategory2) {
    if (typeof window.getCategory === "function") {
      return window.getCategory(itemCategory, itemCategory2);
    }
    // Fallback if getCategory is not available
    return itemCategory || "Other";
  }

  function bindCardWishlistClicks() {
    document.addEventListener(
      "click",
      async (e) => {
        const btn = e.target.closest('[data-wishlist-trigger="card"]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const isAdded = btn.getAttribute("data-added") === "true";
        const productId = btn.getAttribute("data-proid");
        const variantId = btn.getAttribute("data-varid");

        try {
          if (isAdded) {
            // Remove from wishlist
            await Core.removeProductFromWishlist({ productId, variantId });
            Core.markButtonAsRemoved(btn);
            
            // Track wishlist remove event
            const source = getWishlistSource();
            const wishlistCount = (window.hulkappsWishlist?.wishlistProductIds?.length || window.hulkappsWishlist?.wishlistItems?.length || 0);
            
            // Get product data for tracking
            const card = btn.closest(".card--product");
            let productData = null;
            if (card) {
              const productSpan = card.querySelector("span.hidden[data-product-json]");
              if (productSpan) {
                try {
                  productData = JSON.parse(productSpan.textContent.trim());
                  console.log(productData);
                } catch (e) {}
              }
            }
            
            let eventData = {
              event: "remove_from_wishlist",
              wishlist_action: "remove_from_wishlist",
              source: source,
              ecommerce: {
                items: [{
                  item_id: productId,
                  item_variant: variantId || "",
                }],
              },
              wishlist_item_count: wishlistCount,
            };
            
            if (productData) {
              const customFields = productData.custom_fields || productData.customFields || {};
              const itemCategory = customFields["d365-division"] || "";
              const itemCategory2 = customFields["d365-web_department"] || "";
              const category = getCategory(itemCategory, itemCategory2);
              
              eventData.ecommerce = {
                currency: "GBP",
                items: [{
                  id: productId,
                  item_name: productData.name || productData.title || "",
                  item_id: productId,
                  item_variant: variantId || productData.variants?.[0]?.sku || "",
                  mapped_category: category,
                  item_category: itemCategory,
                  item_category2: itemCategory2,
                  item_brand: productData.brand || productData.vendor || "",
                  price: (productData.price || 0) / 100,
                  source: source,
                }]
              };
            }
            
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push(eventData);
          } else {
            // Add to wishlist - open modal for variant selection
            const card = btn.closest(".card--product");

            // find the matching <quick-buy> for this product
            let qb = card?.querySelector("quick-buy");
            if (!qb) {
              qb = document.querySelector(
                `quick-buy[data-product="${productId}"]`
              );
            }
            if (!qb) {
              return;
            }

            // mark intent on the instance
            qb._wishlistSourceButton = btn;
            qb.isWishlistMode = true;

            // If modal already exists, toggle UI now and open it, bypassing theme opener
            const existing = document.getElementById(qb.targetModalId);
            if (existing) {
              qb.modal = existing;
              if (typeof qb.__applyWishlistUIMode === "function") {
                qb.__applyWishlistUIMode();
              }
              window.Woolman?.ModalsAndDrawers?.showModalOrDrawer?.(
                qb.targetModalId
              );
              return;
            }

            // No modal yet: call the normal opener (our override will carry wishlist mode through)
            const opener = qb.onModalRequestOpen || qb.onOpen || function () {};
            opener.call(qb, new Event("open"));
            return;
          }

          // Update header count after wishlist changes
          updateHeaderWishlistCount();
        } catch (err) {
          console.error(
            isAdded
              ? "Remove from wishlist failed:"
              : "Add to wishlist failed:",
            err
          );
        }
      },
      true
    );
  }

  /* ---------------- Cart: move to default wishlist ---------------- */

  function bindCartSaveForLater() {
    document.addEventListener("click", async (e) => {
      const btn =
        e.target.closest("[data-hulkSaveForLater]") ||
        e.target.closest('[data-wishlist-trigger="cart"]');
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const productId =
        btn.getAttribute("data-product-id") || btn.getAttribute("data-proid");
      const variantId =
        btn.getAttribute("data-variant-id") || btn.getAttribute("data-varid");
      const lineRaw = btn.getAttribute("data-line");
      const line = lineRaw ? Number(lineRaw) : null;
      const isAdded = btn.getAttribute("data-added") === "true";

      try {
        if (isAdded) {
          // Remove from wishlist
          await Core.removeProductFromWishlist({ productId, variantId });
          Core.markButtonAsRemoved(btn);
          
          // Track wishlist remove event
          const source = getWishlistSource();
          const wishlistCount = (window.hulkappsWishlist?.wishlistProductIds?.length || window.hulkappsWishlist?.wishlistItems?.length || 0);
          
          let eventData = {
            event: "remove_from_wishlist",
            wishlist_action: "remove_from_wishlist",
            source: "Cart",
            ecommerce: {
              items: [{
                item_id: productId,
                item_variant: variantId || "",
              }],
            },
            wishlist_item_count: wishlistCount,
          };
          
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push(eventData);
        } else {
          // Add to wishlist
          const result = await Core.addProductToDefaultWishlist({ productId, variantId });
          // Get the actual variant ID from the response if available
          const actualVariantId = result?.data?.variant_id || variantId;
          Core.markButtonAsAdded(btn, actualVariantId);
          
          // Track successful wishlist add
          const source = getWishlistSource();
          const wishlistCount = (window.hulkappsWishlist?.wishlistProductIds?.length || window.hulkappsWishlist?.wishlistItems?.length || 0);
          
          let eventData = {
            event: "add_to_wishlist",
            wishlist_action: "add_to_wishlist_success",
            source: "Cart",
            ecommerce: {
              items: [{
              item_id: productId,
              item_variant: actualVariantId || variantId || "",
              }],
            },
            wishlist_item_count: wishlistCount,
          };
          
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push(eventData);

          // If this was from cart line item, remove from cart
          if (line) {
            await fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ line, quantity: 0 }),
            });
            location.reload();
            return; // Don't update UI since page will reload
          }
        }

        // Update wishlist button states
        try {
        } catch (e) {
          hydrateCartWishlistHearts();
          hydrateCardWishlistHearts();
        }

        // Update header count after wishlist changes
        updateHeaderWishlistCount();
      } catch (err) {
        console.error(
          isAdded ? "Remove from wishlist failed:" : "Save for later failed:",
          err
        );
      }
    });
  }

  // Color cart hearts that are already in wishlist (on load / re-render)
  function hydrateCartWishlistHearts() {
    const ids = window.hulkappsWishlist?.wishlistProductIds || [];
    if (!ids.length) return;

    document
      .querySelectorAll('[data-wishlist-trigger="cart"]')
      .forEach((btn) => {
        const pid = parseInt(
          btn.getAttribute("data-product-id") ||
            btn.getAttribute("data-proid") ||
            "",
          10
        );
        if (!Number.isNaN(pid) && ids.includes(pid)) {
          Core.markButtonAsAdded(btn);
        }
      });
  }

  // Mark card wishlist hearts that are already in wishlist
  function hydrateCardWishlistHearts() {
    const ids = window.hulkappsWishlist?.wishlistProductIds || [];
    if (!ids.length) return;

    document
      .querySelectorAll('[data-wishlist-trigger="card"]')
      .forEach((btn) => {
        const pid = parseInt(btn.getAttribute("data-proid") || "", 10);
        if (!Number.isNaN(pid) && ids.includes(pid)) {
          // Set the correct variant ID from wishlist data
          const variantId = findWishlistVariantId(pid);
          if (variantId) {
            btn.setAttribute("data-varid", variantId);
          }
          Core.markButtonAsAdded(btn);
        }
      });
  }

  // Check whether a specific product+variant combo is currently in the wishlist
  function isVariantInWishlist(productId, variantId) {
    const items = window.hulkappsWishlist?.wishlistItems || [];
    const pid = Number(productId);
    if (!pid || Number.isNaN(pid)) return false;
    const vid = variantId ? Number(variantId) : null;
    return items.some((item) => {
      if (item.productId !== pid) return false;
      if (vid) return item.variantId === vid;
      return !item.variantId;
    });
  }

  // Sync the quick-buy modal's own wishlist button to reflect the currently selected variant.
  // Hulk mutates the button's inner markup after a click (injects "Added to wishlist"
  // into the first child element, hides `[data-wishlist-text-add]`, etc). It doesn't
  // re-evaluate on variant change, so we snapshot the pristine inner HTML on first
  // sighting and restore it whenever the selected variant isn't in the wishlist.
  function hydrateQuickbuyWishlistButton(modalRoot) {
    if (!modalRoot) return;
    const btn = modalRoot.querySelector('[data-wishlist-trigger="quickbuy"]');
    if (!btn) return;

    if (!btn.__wlPristineHTML) {
      btn.__wlPristineHTML = btn.innerHTML;
    }

    const variantInput = modalRoot.querySelector('input[name="id"]');
    const productId = btn.getAttribute("data-proid");
    const currentVariantId =
      variantInput?.value || btn.getAttribute("data-varid");
    if (currentVariantId) btn.setAttribute("data-varid", currentVariantId);

    const inWishlist = isVariantInWishlist(productId, currentVariantId);
    // Always restore from pristine first so we control the label rather than
    // guessing what Hulk mutated on a previous click.
    btn.innerHTML = btn.__wlPristineHTML;
    const labelEl = btn.querySelector("[data-wishlist-text-add]");
    if (inWishlist) {
      const addedLabel =
        btn.getAttribute("data-wishlist-label-added") || "Added to Wishlist";
      if (labelEl) labelEl.textContent = addedLabel;
      Core.markButtonAsAdded(btn, currentVariantId);
    } else {
      const addLabel =
        btn.getAttribute("data-wishlist-label-add") || "Add to Wishlist";
      if (labelEl) labelEl.textContent = addLabel;
      btn.setAttribute("data-added", "false");
      btn.href = "#";
    }
    setQuickbuyWishlistButtonDisabled(btn, inWishlist);
  }

  // Once the current variant is in the wishlist, we mark the button as "added".
  // For guests we also lock it so a second click can't remove the item (they
  // can't manage the list without logging in anyway). Logged-in users keep the
  // button clickable so Hulk's own click handler can open the wishlist modal.
  function setQuickbuyWishlistButtonDisabled(btn, added) {
    if (!btn) return;
    const isLoggedIn = !!window.hulkappsWishlist?.customerId;
    const lock = added && !isLoggedIn;
    btn.classList.toggle("wishlist-btn--added-disabled", lock);
    if (lock) {
      btn.setAttribute("aria-disabled", "true");
      btn.setAttribute("tabindex", "-1");
      btn.style.pointerEvents = "none";
    } else {
      btn.removeAttribute("aria-disabled");
      btn.removeAttribute("tabindex");
      btn.style.removeProperty("pointer-events");
    }
  }

  /* ---------------- Quick-buy modal wishlist button ---------------- */

  // Hulk's own JS handles the modal wishlist button click via `data-wishList`
  // (same code path as the PDP button — uses the Shopify app proxy, same-origin,
  // no CORS). We observe the button's `data-added` attribute so we can:
  //   • sync matching PLP card hearts
  //   • mirror the change into our internal wishlist arrays
  //   • fire dataLayer analytics events
  //
  // A user-action flag disambiguates real toggles from hydration writes so we
  // don't fire analytics on modal open or variant change.
  function attachQuickbuyWishlistObserver(modalRoot) {
    if (!modalRoot) return;
    const btn = modalRoot.querySelector('[data-wishlist-trigger="quickbuy"]');
    if (!btn) return;
    if (btn.__wlObserverAttached) return;
    btn.__wlObserverAttached = true;

    btn.addEventListener("click", () => {
      btn.__wlUserActionPending = true;
      // Safety: clear the flag if no state change lands within 5s (e.g. Hulk
      // errored silently) so future hydrations aren't misread as user actions.
      clearTimeout(btn.__wlUserActionTimer);
      btn.__wlUserActionTimer = setTimeout(() => {
        btn.__wlUserActionPending = false;
      }, 5000);
    });

    const obs = new MutationObserver(() => {
      if (!btn.__wlUserActionPending) return;
      btn.__wlUserActionPending = false;
      clearTimeout(btn.__wlUserActionTimer);

      const nowAdded = btn.getAttribute("data-added") === "true";
      const productId = btn.getAttribute("data-proid");
      const variantInput = modalRoot.querySelector('input[name="id"]');
      const variantId =
        variantInput?.value || btn.getAttribute("data-varid") || "";

      // Mirror the toggle into our internal arrays (Hulk manages its own state
      // but our card/cart heart logic reads `window.hulkappsWishlist.*`).
      const pid = Number(productId);
      const vid = variantId ? Number(variantId) : null;
      if (!Number.isNaN(pid)) {
        const ids = (window.hulkappsWishlist.wishlistProductIds =
          window.hulkappsWishlist.wishlistProductIds || []);
        const items = (window.hulkappsWishlist.wishlistItems =
          window.hulkappsWishlist.wishlistItems || []);

        if (nowAdded) {
          if (!ids.includes(pid)) ids.push(pid);
          if (!items.some((i) => i.productId === pid && i.variantId === vid)) {
            items.push({ productId: pid, variantId: vid });
          }
        } else {
          const idx = ids.indexOf(pid);
          if (idx > -1) ids.splice(idx, 1);
          const itemIdx = items.findIndex(
            (i) => i.productId === pid && i.variantId === vid
          );
          if (itemIdx > -1) items.splice(itemIdx, 1);
        }
      }

      // Sync matching PLP card hearts
      document
        .querySelectorAll(
          `[data-wishlist-trigger="card"][data-proid="${productId}"]`
        )
        .forEach((cardBtn) => {
          if (nowAdded) {
            if (variantId) cardBtn.setAttribute("data-varid", variantId);
            Core.markButtonAsAdded(cardBtn, variantId);
          } else {
            Core.markButtonAsRemoved(cardBtn);
          }
        });

      // Lock the button immediately after a successful add so a second click
      // can't accidentally remove the item they just saved.
      setQuickbuyWishlistButtonDisabled(btn, nowAdded);

      updateHeaderWishlistCount();
      hydrateCartWishlistHearts();

      // Analytics
      const source = getWishlistSource();
      const wishlistCount =
        window.hulkappsWishlist?.wishlistProductIds?.length ||
        window.hulkappsWishlist?.wishlistItems?.length ||
        0;

      if (nowAdded) {
        let productData = null;
        const productSpan = modalRoot.querySelector(
          "span.hidden[data-product-json]"
        );
        if (productSpan) {
          try {
            productData = JSON.parse(productSpan.textContent.trim());
          } catch (_) {}
        }

        let eventData = {
          event: "add_to_wishlist",
          wishlist_action: "add_to_wishlist_success",
          source: source,
          ecommerce: {
            items: [
              { item_id: productId, item_variant: variantId || "" },
            ],
          },
          wishlist_item_count: wishlistCount,
        };

        if (productData) {
          const customFields =
            productData.custom_fields || productData.customFields || {};
          const itemCategory = customFields["d365-division"] || "";
          const itemCategory2 = customFields["d365-web_department"] || "";
          const category = getCategory(itemCategory, itemCategory2);

          eventData.ecommerce = {
            currency: "GBP",
            items: [
              {
                id: productId,
                item_name: productData.name || productData.title || "",
                item_id: productId,
                item_variant:
                  variantId || productData.variants?.[0]?.sku || "",
                mapped_category: category,
                item_category: itemCategory,
                item_category2: itemCategory2,
                item_brand: productData.brand || productData.vendor || "",
                price: (productData.price || 0) / 100,
                source: source,
              },
            ],
          };
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(eventData);
      } else {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "remove_from_wishlist",
          wishlist_action: "remove_from_wishlist",
          source: source,
          ecommerce: {
            items: [
              { item_id: productId, item_variant: variantId || "" },
            ],
          },
          wishlist_item_count: wishlistCount,
        });
      }
    });

    obs.observe(btn, { attributes: true, attributeFilter: ["data-added"] });
  }

  // Watch for drawer/cart DOM updates and re-hydrate hearts

  function observeCartForUpdates() {
    let timer = null;
    const needsHydrate = (node) => {
      if (node.nodeType !== 1) return false;
      if (node.matches?.(".cart, .cart-drawer")) return true;
      return !!node.querySelector?.('[data-wishlist-trigger="cart"]');
    };

    const obs = new MutationObserver((muts) => {
      for (const m of muts) {
        for (const n of m.addedNodes) {
          if (needsHydrate(n)) {
            clearTimeout(timer);
            timer = setTimeout(hydrateCartWishlistHearts, 120);
            return;
          }
        }
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  // Nosto recommendation cards get their wishlist buttons from Liquid
  // (card-product.liquid). We only need to observe the DOM so we can
  // hydrate newly-inserted buttons with the correct wishlist state.
  function observeNewCards() {
    let pending = null;
    const obs = new MutationObserver((muts) => {
      let hasNewCard = false;
      for (const m of muts) {
        for (const n of m.addedNodes) {
          if (n.nodeType !== 1) continue;
          if (
            n.querySelector?.('[data-wishlist-trigger="card"]') ||
            n.matches?.('[data-wishlist-trigger="card"]')
          ) {
            hasNewCard = true;
            break;
          }
        }
        if (hasNewCard) break;
      }
      if (!hasNewCard) return;
      clearTimeout(pending);
      pending = setTimeout(() => hydrateCardWishlistHearts(), 150);
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  /* ---------------- QuickBuy augmentation (no duplicate helpers) ---------------- */

  function augmentQuickBuyPrototype() {
    const Ctor = customElements.get("quick-buy");
    if (!Ctor) return;

    const proto = Ctor.prototype;
    if (proto.__wlPatched) return;
    proto.__wlPatched = true;

    const origOnModalRequestOpen = proto.onModalRequestOpen;
    const origHookEvents = proto.hookEvents;
    const origOnVariantChange = proto.onVariantChange;

    // Helper: sync the modal's own wishlist button with the currently selected variant.
    // The ajax modal renders a `[data-wishlist-trigger="quickbuy"]` button; we simply
    // keep its state in sync — no form-hiding, no injected save button.
    proto.__applyWishlistUIMode = function () {
      const modalRoot =
        this.modal || document.getElementById(this.targetModalId);
      if (!modalRoot) return;

      // Ensure the product form is visible even if a stale flag was set earlier.
      const productFormComponent =
        modalRoot.querySelector("product-form") ||
        modalRoot.querySelector('[id^="product-form-"]') ||
        null;
      if (productFormComponent) productFormComponent.style.display = "";

      hydrateQuickbuyWishlistButton(modalRoot);
      attachQuickbuyWishlistObserver(modalRoot);

      // Nudge Hulk's own script to bind to the newly-inserted `data-wishList`
      // button so clicks route through its (same-origin) app-proxy code path.
      try {
        window.HulkappWishlist?._updateWishlistButton?.();
      } catch (_) {}
    };

    // Stable entry for product cards to open in wishlist mode
    proto.openForWishlist = function (sourceBtn) {
      this._wishlistSourceButton = sourceBtn || null;
      this.isWishlistMode = true;
      const opener = this.onModalRequestOpen || this.onOpen || function () {};
      return opener.call(this, new Event("open"));
    };

    // Prefer current click target; only fall back to cached source for openForWishlist()
    if (typeof origOnModalRequestOpen === "function") {
      proto.onModalRequestOpen = function (event) {
        event?.preventDefault?.();
        const currentSrc = event?.target?.closest?.(
          ".card-wishlist-btn, [data-wishlist]"
        );

        // Use cached source only if:
        // 1. Current click is from a wishlist button, OR
        // 2. We have a cached source AND the current event is synthetic (from wishlist flow)
        const isSyntheticEvent = event.type === "open" && !event.target;
        const srcBtn =
          currentSrc || (isSyntheticEvent ? this._wishlistSourceButton : null);

        if (srcBtn) {
          this.isWishlistMode = true;
          this.wishListSourceButton = srcBtn;
        } else {
          this.isWishlistMode = false;
          this.wishListSourceButton = null;
          // Clear any cached wishlist source when opening in normal mode
          this._wishlistSourceButton = null;
        }
        // one-shot the cache so previous opens can't leak
        this._wishlistSourceButton = null;

        // If the modal already exists, toggle UI now and show it
        const existing = document.getElementById(this.targetModalId);
        if (existing) {
          this.modal = existing;
          this.__applyWishlistUIMode();
          window.Woolman?.ModalsAndDrawers?.showModalOrDrawer?.(
            this.targetModalId
          );
          return;
        }

        // Otherwise build via original (hookEvents will apply wishlist UI)
        return origOnModalRequestOpen.call(this, event || new Event("open"));
      };
    }

    // After modal build, inject/remove wishlist UI based on current mode
    if (typeof origHookEvents === "function") {
      proto.hookEvents = function (...args) {
        origHookEvents.apply(this, args);

        // Always (re)apply UI on first build
        this.__applyWishlistUIMode();

        // Reset wishlist mode on close (bind once per element)
        if (!this.__wlCloseBound) {
          const resetWishlistMode = () => {
            this.isWishlistMode = false;
            this._wishlistSourceButton = null;
            this.wishListSourceButton = null;
            // Also reset any existing modal UI to normal mode
            if (this.modal) {
              this.__applyWishlistUIMode();
            }
          };

          this.modal
            ?.querySelector?.("[data-overlay]")
            ?.addEventListener("click", resetWishlistMode);
          this.modal
            ?.querySelector?.(
              "[data-close], .modal__close, button[aria-label='Close']"
            )
            ?.addEventListener("click", resetWishlistMode);
          this.addEventListener?.("close", resetWishlistMode);

          this.__wlCloseBound = true;
        }
      };
    }

    // Keep the modal's own wishlist button in sync with the currently selected variant
    if (typeof origOnVariantChange === "function") {
      proto.onVariantChange = function (...args) {
        origOnVariantChange.apply(this, args);
        const modalRoot =
          this.modal || document.getElementById(this.targetModalId);
        if (modalRoot) hydrateQuickbuyWishlistButton(modalRoot);
      };
    }
  }

  // Patch QuickBuy no matter the load order
  if (customElements.get("quick-buy")) {
    augmentQuickBuyPrototype();
  } else {
    customElements.whenDefined("quick-buy").then(augmentQuickBuyPrototype);
  }

  /* ---------------- Boot ---------------- */

  let _guestMigratedThisSession = false;

  async function migrateGuestToCustomerIfNeeded() {
    if (_guestMigratedThisSession) return;

    const cid = window.hulkappsWishlist?.customerId;
    const gid = getSavedGuestUuid() || window.hulkappsWishlist?.guestId || null;
    if (!cid || !gid) return;

    try {
      // 0) Try Hulk’s helper, but do NOT return; we’ll still do manual migration to be sure
      try {
        await window.HulkappWishlist?._addFromGuestToData?.();
      } catch (e) {
        // ignore; we’ll handle manually
      }

      // 1) Read guest items
      const guestUrl = Core.createUrlWithParams(
        "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/products/guest",
        { uuid: gid }
      );
      const guestResp = await Core.makeRequest(
        guestUrl,
        Core.createHeaders(false)
      );
      const items = Array.isArray(guestResp?.data) ? guestResp.data : [];

      if (!items.length) {
        // Even if none, clear the stale UUID so future logouts don’t 404
        clearGuestState();
        await Core.fetchWishlistData();
        _guestMigratedThisSession = true;
        return;
      }

      // 2) Ensure default list and add each item
      const listId = await Core.getDefaultWishlistId();

      for (const it of items) {
        const payload = {
          product_id: String(it.product_id),
          customer_id: String(cid),
          wishlist_ids: [Number(listId)],
          ...(it.variant_id ? { variant_id: String(it.variant_id) } : {}),
        };
        const res = await fetch(
          "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/add_product",
          {
            method: "POST",
            headers: Core.createHeaders(),
            body: JSON.stringify(payload),
          }
        );
        if (!res.ok && res.status !== 422) {
          const t = await res.text().catch(() => "");
          console.warn("migrate add failed", res.status, t, payload);
        }
      }

      // 3) Now it’s safe to clear guest state and refetch lists
      clearGuestState();

      // Fetch twice with a tiny delay to avoid timing issues on Hulk’s side
      await Core.fetchWishlistData();
      setTimeout(() => Core.fetchWishlistData().catch(() => {}), 250);

      _guestMigratedThisSession = true;
    } catch (err) {
    }
  }

  // nosto-search.js dispatches this after rendering search/collection grids.
  // Buttons already exist in the template markup; we just hydrate state.
  document.addEventListener("nosto:rendered", () => {
    whenWishlistLoaded(() => hydrateCardWishlistHearts());
  });

  document.addEventListener("DOMContentLoaded", async () => {
    if (!window.hulkappsWishlist.wishlistProductIds) {
      window.hulkappsWishlist.wishlistProductIds = [];
    }
    if (!window.hulkappsWishlist.wishlistItems) {
      window.hulkappsWishlist.wishlistItems = [];
    }

    if (window.hulkappsWishlist?.customerId) {
      await migrateGuestToCustomerIfNeeded();
    } else {
      if (!window.hulkappsWishlist?.guestId) {
        const cached = getSavedGuestUuid();
        if (cached) saveGuestUuid(cached);
      }
    }

    bindCardWishlistClicks();
    bindCartSaveForLater();
    watchGuestIdAndLoadOnce();
    observeCartForUpdates();
    observeNewCards();

    whenWishlistLoaded(() => {
      hydrateCartWishlistHearts();
      hydrateCardWishlistHearts();
    });
  });
})();
